import axios from '@/api/axios';
import {
  AllVesselsResponse,
  ExpiredData,
  Vessel,
  VesselExpiredResponse,
  VesselHistory,
  VesselHistoryMeteorology,
  VesselHistoryMeteorologyResponse,
  VesselHistoryReport,
  VesselHistoryReportResponse,
  VesselHistoryResponse,
  VesselResponse,
} from '@/types/api';
import apiConfig from '@/config/url-api';

import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import CoordinateUtils from '@/utils/coordinate';
import { EditVesselForm } from '@/types/form';

export const getAllVessels = async (): Promise<Vessel[]> => {
  const response = await axios.get<AllVesselsResponse>(apiConfig.vessels.all);
  return response.data.data;
};

export const useQueryAllVessels = (): UseQueryResult<Vessel[], Error> => {
  const vessels = useQuery({
    queryKey: ['AllVessel'],
    queryFn: getAllVessels,
  });

  return vessels;
};

export const getDetailVessels = async (
  id: string,
  startDate: string,
  endDate: string,
  withMeteorology: boolean,
  type: string,
): Promise<Vessel> => {
  const response = await axios.get<VesselHistoryResponse>(
    apiConfig.vessels.getDetail.format({
      id: id,
      startDate: startDate,
      endDate: endDate,
      withMeteorology: withMeteorology,
      type: type,
    }),
  );

  /**
   * Convert dd to dms
   */

  let data = response.data.data;
  let dataHistory = changeCoordinate(data.vessel_history);
  data.vessel_history = dataHistory;

  return data;
};

export const getDetailVesselReport = async (
  id: string,
  startDate: string,
  endDate: string,
  withMeteorology: boolean,
  type: string,
): Promise<VesselHistoryReport> => {
  const response = await axios.get<VesselHistoryReportResponse>(
    apiConfig.vessels.getReport.format({
      id: id,
      startDate: startDate,
      endDate: endDate,
      withMeteorology: withMeteorology,
      type: type,
    }),
  );
  return response.data.data;
};

export const getDetailVesselReportExport = async (
  id: string,
  startDate: string,
  endDate: string,
  withMeteorology: boolean,
  type: string,
  export_type: string,
  is_dms: string,
): Promise<any> => {
  const response = await axios.get<any>(
    apiConfig.vessels.getReportExport.format({
      id: id,
      startDate: startDate,
      endDate: endDate,
      withMeteorology: withMeteorology,
      type: type,
      export_type: export_type,
      is_dms: is_dms,
    }),
    {
      responseType: 'blob',
    },
  );
  return response;
};

export const exportVoyagesReportApi = async (
  id: string,
  payload: {
    from_date?: string;
    to_date?: string;
    export_type: 'pdf' | 'xlsx';
    voyage_keys?: string[];
    voyages?: any[];
    save_to_space?: boolean;
  },
): Promise<any> => {
  const response = await axios.post<any>(
    `v2/vessels/${id}/report/voyages/export`,
    payload,
    {
      responseType: 'blob',
    },
  );
  return response;
};

export const getDetailMeteorologyVesselHistory = async (
  id: string,
  idHistory: number | undefined,
): Promise<VesselHistoryMeteorology> => {
  const response = await axios.get<VesselHistoryMeteorologyResponse>(
    apiConfig.vessels.getDetail.format({ id: id, idHistory: idHistory }),
  );
  return response.data.data;
};

export const getExpiredVessel = async (
  startDate: string | null,
  endDate: string | null,
  vessel: string | null,
): Promise<ExpiredData[]> => {
  const response = await axios.get<VesselExpiredResponse>(
    apiConfig.vessels.getExpired.format({
      startDate: startDate,
      endDate: endDate,
      vessel: vessel,
    }),
  );
  return response.data.data;
};

export const useQueryGetDetailVessel = (
  id: string,
  startDate: string,
  endDate: string,
  withMeteorology: boolean,
  type: string,
): UseQueryResult<Vessel, Error> => {
  const vessels = useQuery({
    queryKey: ['DetailVessels', id],
    queryFn: () => getDetailVessels(id, startDate, endDate, withMeteorology, type),
    enabled: false,
    staleTime: 1000 * 60 * 5,
  });

  return vessels;
};

export const useQueryGetDetailMeteorologyVesselHistory = (
  id: string,
  idHistory: number | undefined,
): UseQueryResult<VesselHistoryMeteorology, Error> => {
  const vessels = useQuery({
    queryKey: ['DetailMeteorologyVesselHistory', id, idHistory],
    queryFn: () => getDetailMeteorologyVesselHistory(id, idHistory),
    enabled: false,
    staleTime: 1000 * 60 * 5,
  });

  return vessels;
};

export const useQueryGetDetailVesselReport = (
  id: string,
  startDate: string,
  endDate: string,
  withMeteorology: boolean,
  type: string,
): UseQueryResult<VesselHistoryReport, Error> => {
  const vessels = useQuery({
    queryKey: ['DetailVesselsReport', id],
    queryFn: () => getDetailVesselReport(id, startDate, endDate, withMeteorology, type),
    enabled: false,
    staleTime: 1000 * 60 * 5,
  });

  return vessels;
};

export const useQueryGetExpiredVessels = (
  startDate: string | null,
  endDate: string | null,
  vessel: string | null,
): UseQueryResult<ExpiredData[], Error> => {
  const expiredVessels = useQuery({
    queryKey: ['ExpiredVessels', startDate, endDate, vessel],
    queryFn: () => getExpiredVessel(startDate, endDate, vessel),
    // enabled: false,
    staleTime: 1000 * 60 * 5,
  });

  return expiredVessels;
};

export const changeCoordinate = (vessel_history: VesselHistory[]) => {
  try {
    if (vessel_history?.length > 0) {
      let tempVesselHistory = vessel_history.map((item: VesselHistory) => {
        if (item?.latitude && item?.longitude) {
          item.latitude_dms = CoordinateUtils.dec2StrLat(parseFloat(item.latitude));
          item.longitude_dms = CoordinateUtils.dec2StrLng(parseFloat(item.longitude));
        }
        return item;
      });

      return tempVesselHistory;
    } else {
      return vessel_history;
    }
  } catch (e) {
    console.log(e);
    return vessel_history;
  }
};


export const updateVessel = async (
  id: string,
  params: EditVesselForm,
): Promise<Vessel> => {
  const response = await axios.post<VesselResponse>(
    apiConfig.vessels.edit.format({ id: id }),
    params,
  );
  return response.data.data;
};

export const useMutationUpdateVessel = () => {
  const queryClient = useQueryClient();

  const updateVesselMutation = useMutation({
    mutationFn: (data: { id: string; params: EditVesselForm }) =>
      updateVessel(data.id, data.params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AllNotificationSettings'] });
    },
  } as UseMutationOptions<Vessel, unknown, { id: string; params: EditVesselForm }, unknown>);

  return updateVesselMutation;
};
